import { useUpdateProductQuantity } from '@/api/api-mutations'
import { useGetOrder, useGetVariant } from '@/api/react-query'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Button } from '@/components/ui/button'
import { orderIdSubject } from '@/contexts/order-context'
import { useI18n } from '@/i18n/i18n-react'
import { useGetSubject } from '@/utils/hooks'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { tenant } from '@/tenants/tenant'
import { Minus, Plus, Trash2 } from 'lucide-react'
import { Image } from '@/components/ui/image'
import { ErrorDisplay } from '@/components/error-display'
import { Helmet } from 'react-helmet-async'

export const Route = createFileRoute('/product/$productId')({
	component: ProductDetail,
	beforeLoad: () => {
		if (!tenant.features.ecomEnabled) {
			throw redirect({ to: '/' })
		}
	}
})

function ProductDetail() {
	const { L } = useI18n()
	const { productId } = Route.useParams()
	const variantUuid = productId!
	const orderUuid = useGetSubject(orderIdSubject)
	const activeOrder = useGetOrder({ orderUuid })
	const variant = useGetVariant({ variantUuid }, {})
	const updateProductQuantity = useUpdateProductQuantity()

	const cartItem = activeOrder.data?.itemGroups
		.flatMap(x => x.items)
		.find(item => item.variantUuid === variant.data?.uuid)

	return (
		<div className='container mx-auto max-w-4xl p-6'>
			<Helmet>
				<title>{variant.data?.title || L.Product.Details.Title()}</title>
				<meta name='description' content={variant.data?.description || L.Product.Details.Description()} />
			</Helmet>
			{variant.isLoading ?
				<div className='flex items-center justify-center py-12'>
					<LoadingSpinner />
				</div>
			: variant.error ?
				<ErrorDisplay refetch={variant.refetch} className='space-y-4' />
			:	<div className='flex flex-col gap-8 lg:flex-row'>
					<div className='w-full lg:w-2/5'>
						<div className='rounded-lg bg-gray-50 p-6'>
							<Image
								src={variant.data?.logoUrl}
								alt={variant.data?.title}
								className='mx-auto aspect-square w-full max-w-sm object-contain'
							/>
						</div>
					</div>
					<div className='flex w-full flex-col lg:w-3/5'>
						<div className='space-y-6'>
							<div>
								<h1 className='text-foreground text-3xl font-bold'>{variant.data?.title}</h1>
								<p className='text-primary mt-2 text-4xl font-bold'>
									{L.Amount.Default({
										currencyCode: variant.data?.currencyCode,
										amount: variant.data?.unitPrice?.gross
									})}
								</p>
							</div>

							{variant.data?.title && (
								<div className='prose prose-sm text-muted-foreground'>
									<p>{variant.data.title}</p>
								</div>
							)}

							{variant && (
								<div className='space-y-4'>
									{
										// isOutOfStock ?
										// 	<div className='flex items-center justify-center rounded-lg bg-destructive/10 py-2'>
										// 		<p className='text-sm font-medium text-destructive'>Out of stock</p>
										// 	</div>
										// :
										cartItem ?
											<div className='bg-card flex items-center justify-between rounded-lg border p-4'>
												<div className='flex items-center gap-3'>
													<Button
														size='icon'
														variant='outline'
														onClick={e => {
															e.preventDefault()
															updateProductQuantity.mutateAsync({
																path: {
																	orderUuid,
																	variantUuid
																},
																body: {
																	quantityChange: -1
																}
															})
														}}
													>
														<Minus className='h-4 w-4' />
													</Button>
													<span className='w-12 text-center text-xl font-medium'>
														{cartItem.quantityInfo.quantity}
													</span>
													<Button
														size='icon'
														variant='outline'
														onClick={e => {
															e.preventDefault()
															updateProductQuantity.mutateAsync({
																path: {
																	orderUuid,
																	variantUuid
																},
																body: {
																	quantityChange: 1
																}
															})
														}}
													>
														<Plus className='h-4 w-4' />
													</Button>
												</div>
												<Button
													variant='destructive'
													size='icon'
													onClick={e => {
														e.preventDefault()
														updateProductQuantity.mutateAsync({
															path: {
																orderUuid,
																variantUuid
															},
															body: {
																removeFromOrder: true
															}
														})
													}}
												>
													<Trash2 className='h-4 w-4' />
												</Button>
											</div>
										:	<Button
												size='lg'
												className='w-full py-4 text-lg'
												onClick={e => {
													e.preventDefault()
													updateProductQuantity.mutateAsync({
														path: {
															orderUuid,
															variantUuid
														},
														body: {
															quantityChange: 1
														}
													})
												}}
											>
												{L.Product.Details.AddToCart()}
											</Button>

									}
								</div>
							)}
						</div>
					</div>
				</div>
			}
		</div>
	)
}
